import React, { useEffect, useState } from "react";
import {
  apiAdminPosts,
  apiAdminCreatePost,
  apiAdminUpdatePost,
  apiAdminDeletePost,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const empty = {
  slug: "", title: "", excerpt: "", body: "",
  coverImage: "", author: "Certified Wellness Research Team", tags: [],
  metaTitle: "", metaDescription: "", published: false,
};

const _slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

const AdminPosts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await apiAdminPosts());
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    try {
      if (form.id) await apiAdminUpdatePost(form.id, form);
      else await apiAdminCreatePost(form);
      toast.success("Post saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await apiAdminDeletePost(id);
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-px w-8 bg-[#b91c1c]" />
            <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Research Journal</span>
          </div>
          <h1 className="font-display text-4xl font-semibold text-[#0a0a0a] tracking-tight">Blog Posts</h1>
          <p className="text-sm text-gray-500 mt-2 font-light">Write lab notes, research updates and compliance commentary. Drafts stay hidden until you publish them.</p>
        </div>
        <Button
          onClick={() => setEditing({ ...empty })}
          className="btn-primary rounded-none h-11 px-6 text-[12px] font-semibold uppercase tracking-[0.18em]"
          data-testid="admin-post-new-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> New Post
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-500 border border-dashed border-gray-200 px-8 py-16 text-center">
          No posts yet. Click "New Post" to write your first lab note.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fafbfc] border-b border-gray-200 text-[10px] uppercase tracking-[0.18em] text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Slug</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 hover:bg-[#fafbfc]" data-testid={`admin-post-row-${p.slug}`}>
                  <td className="px-4 py-3 font-semibold text-[#0a0a0a]">{p.title}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">/{p.slug}</td>
                  <td className="px-4 py-3">
                    {p.published ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-green-700 bg-green-100 px-2 py-1">
                        <Eye className="h-3 w-3" /> Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-700 bg-gray-100 px-2 py-1">
                        <EyeOff className="h-3 w-3" /> Draft
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(p.updatedAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-3">
                      {p.published && (
                        <a
                          href={`/blog/${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-gray-400 hover:text-[#0a0a0a]"
                          title="View on site"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button onClick={() => setEditing(p)} className="text-gray-500 hover:text-[#0a0a0a]" title="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => onDelete(p.id)} className="text-gray-500 hover:text-[#b91c1c]" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <PostEditor post={editing} onClose={() => setEditing(null)} onSave={onSave} />}
    </div>
  );
};

const PostEditor = ({ post, onClose, onSave }) => {
  const [form, setForm] = useState(post);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-slug from title when slug is empty (only on creation).
  const onTitleChange = (val) => {
    setForm((f) => ({ ...f, title: val, slug: f.slug || _slugify(val) }));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 py-4 border-b border-gray-200">
          <DialogTitle className="font-display text-2xl font-semibold text-[#0a0a0a]">
            {form.id ? "Edit Post" : "New Post"}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 mt-1">
            Lab notes, research updates, or compliance commentary. Drafts stay hidden until you publish.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); onSave(form); }}
          className="p-6 space-y-5"
        >
          <div>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => onTitleChange(e.target.value)}
              required
              className="mt-1 rounded-none"
              data-testid="admin-post-title-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>URL Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => set("slug", _slugify(e.target.value))}
                placeholder="e.g. inside-our-coa-process"
                required
                className="mt-1 rounded-none font-mono text-sm"
                data-testid="admin-post-slug-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Lives at <span className="font-mono">/blog/{form.slug || "your-slug"}</span>
              </p>
            </div>
            <div>
              <Label>Author</Label>
              <Input
                value={form.author}
                onChange={(e) => set("author", e.target.value)}
                className="mt-1 rounded-none"
              />
            </div>
          </div>

          <div>
            <Label>Excerpt</Label>
            <Textarea
              rows={2}
              value={form.excerpt}
              onChange={(e) => set("excerpt", e.target.value)}
              placeholder="1–2 sentence summary shown on the blog index and used as the default meta description."
              className="mt-1 rounded-none"
              data-testid="admin-post-excerpt-input"
            />
          </div>

          <div>
            <Label>Cover Image URL (optional)</Label>
            <Input
              value={form.coverImage}
              onChange={(e) => set("coverImage", e.target.value)}
              placeholder="https://…  or  /api/static/images/…"
              className="mt-1 rounded-none"
            />
          </div>

          <div>
            <Label>Body</Label>
            <Textarea
              rows={14}
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
              required
              placeholder={"Write in plain English. Separate paragraphs with a blank line.\n\nStart a heading with '## '.\n\nStart a bullet list with '- '."}
              className="mt-1 rounded-none font-mono text-[13px] leading-relaxed"
              data-testid="admin-post-body-input"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Blank line = new paragraph. <span className="font-mono">## Heading</span> = section. <span className="font-mono">- item</span> = bullet.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-200 space-y-4">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-gray-500">SEO Overrides (optional)</Label>
            <div>
              <Label className="text-xs">Meta Title</Label>
              <Input
                value={form.metaTitle}
                onChange={(e) => set("metaTitle", e.target.value)}
                placeholder="Leave blank to use the post title"
                className="mt-1 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs">Meta Description</Label>
              <Textarea
                rows={2}
                value={form.metaDescription}
                onChange={(e) => set("metaDescription", e.target.value)}
                placeholder="Leave blank to use the excerpt"
                className="mt-1 rounded-none"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
            <label className="inline-flex items-center gap-3 cursor-pointer" data-testid="admin-post-published-toggle">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => set("published", e.target.checked)}
                className="h-4 w-4 accent-[#b91c1c]"
              />
              <span className="text-sm text-[#0a0a0a]">
                {form.published ? "Publish — visible on /blog" : "Save as draft (hidden from public)"}
              </span>
            </label>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} className="rounded-none h-11 px-6 text-[12px] uppercase tracking-[0.18em]">Cancel</Button>
              <Button
                type="submit"
                className="btn-primary rounded-none h-11 px-6 text-[12px] font-semibold uppercase tracking-[0.18em]"
                data-testid="admin-post-save-btn"
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPosts;
